import React, { useEffect, useId, useState } from "react";
import CartIcon from "src/assets/images/website-images/addcart-image-new.png";
import CalenderIcon from "src/assets/images/website-images/calendar-date.png";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "src/Store/store";
import { RootState } from "src/Store/store";
import { addItem, removeItem } from "src/Store/cartSlice";
import { useNavigate } from "react-router-dom";
import CounterComponent from "../Customer/ProductsAndServicesComponents/CounterComponent";
import { Loader2, Trash, Trash2, TrashIcon } from "lucide-react";
import { Tooltip } from "@mui/material";
import OutOfStackTag from "./Widget/OutOfStackTag";
import httpRequest from "src/helpers/httpRequest";
import { removeItemFromCart, setCartItems } from "src/Store/Cart";
import { toast } from "react-toastify";
import { CartItemToast } from "src/lib/constans";

interface IProps {
  icon?: "CartPlus" | "Calender";
  onClick?: (e: React.MouseEvent<HTMLDivElement>, id?: string) => void;
  disabled?: boolean;
  id?: string;
  type?: "diagnostic" | "pharmacy" | "homecare" | "doctor";
  productId?: string;
  quantity?: number;
  itemType?: "product" | "package";
  stockQuantity?: number;
  prescriptionRequired?: boolean;
  index?: number;
  title?: string;
}

const AddCartButton = ({
  icon = "CartPlus",
  onClick,
  disabled = false,
  id = "",
  type = "pharmacy",
  productId = "",
  quantity = 1,
  itemType = "product",
  stockQuantity = 1,
  prescriptionRequired = false,
  index,
  title,
}: IProps) => {
  const directId = useId();
  let iconImage = {
    CartPlus: CartIcon,
    Calender: CalenderIcon,
  };

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { cartItems } = useSelector((store: RootState) => store.cart);
  const { pharmacyProducts = [], diagnosticProducts = [] } = useSelector(
    (store: RootState) => store.cartSlice
  );
  const { isLogged } = useSelector((store: RootState) => store.appReducer);
  const [isAdded, setIsAdded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
  const [prescriptionRequiredModal, setPrescriptionRequiredModal] =
    useState<boolean>(false);
  const checkPrescribed = () => {
    if (stockQuantity === 0) {
      toast.error("This product is out of stock");
      return;
    }
    addToCart();
  };
  const addToCart = async () => {
    if (isLoading) return;
    if (isLogged) {
      setIsLoading(true);
      const { err, res } = await httpRequest({
        path: "/cart/add",
        method: "post",
        params: {
          items: [
            {
              quantity: 1,
              productId: productId,
              type: type === "pharmacy" ? "pharmacy" : itemType,
            },
          ],
        },
      });
      if (err) {
        console.log(err, "err");
        toast.error(CartItemToast["error"][`${type}-${itemType}` as keyof typeof CartItemToast["error"]]);
        return;
      } else {
        toast.success(CartItemToast["success"][`${type}-${itemType}` as keyof typeof CartItemToast["success"]]);
        const items = res.cartItems || [];
        dispatch(setCartItems([...items]));
      }
    } else {
      if (type === "diagnostic") {
        dispatch(
          addItem({
            category: "diagnosticProducts",
            product: { _id: productId, quantity: 1, itemType },
          })
        );
      } else {
        dispatch(
          addItem({
            category: "pharmacyProducts",
            product: { _id: productId, quantity: 1 },
          })
        );
      }
    }
    setPrescriptionRequiredModal(false);
    setIsLoading(false);
  };

  const handleRemove = async () => {
    if (isLogged) {
      const item = cartItems.find((v: any) => v.productId === productId);
      if (!item) {
        return;
      }
      setDeleteLoader(false);
      const { err, res } = await httpRequest({
        path: `/cart/${item?._id}`,
        method: "delete",
        params: {},
      });
      if (err) {
        toast.error(CartItemToast["deleteError"][`${type}-${itemType}` as keyof typeof CartItemToast["deleteError"]]);
        return;
      }
      toast.success(CartItemToast["delete"][`${type}-${itemType}` as keyof typeof CartItemToast["delete"]]);
      dispatch(removeItemFromCart([productId]));
      setIsAdded(false);
      setDeleteLoader(false);
    } else {
      if (type === "diagnostic") {
        dispatch(removeItem({ category: "diagnosticProducts", productId }));
        setIsAdded(false);
      } else {
        dispatch(removeItem({ category: "pharmacyProducts", productId }));
        setIsAdded(false);
      }
    }
  };

  useEffect(() => {
    if (isLogged) {
      setIsAdded(cartItems?.some((v: any) => v.productId === productId));
    } else {
      if (type === "pharmacy") {
        setIsAdded(pharmacyProducts?.some((v) => v._id === productId));
      } else if (type === "diagnostic") {
        setIsAdded(diagnosticProducts?.some((v) => v._id === productId));
      }
    }
  }, [
    cartItems,
    productId,
    type,
    pharmacyProducts,
    diagnosticProducts,
    // isLogged,
  ]);

  if (quantity <= 0 || stockQuantity <= 0) return <OutOfStackTag />;

  return (
    <>
      {!isAdded ||
      type === "diagnostic" ||
      type === "homecare" ||
      type === "doctor" ? (
        <Tooltip
          title={
            title || type === "homecare"
              ? "Book Appointment"
              : isAdded
              ? "Remove from Cart"
              : "Add to Cart"
          }
        >
          <div
            className={`flex gap-1 animate-scale-in items-center cursor-pointer justify-center border 2xl:border-2 border-[#007A71] rounded-full w-[30px] h-[20px] xl:w-[35px] xl:h-[25px] 2xl:w-[50px] 2xl:h-[30px] ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (disabled) return;
              if (type === "homecare" || type === "doctor") {
                onClick?.(e, productId);
                return;
              }
              onClick?.(e, productId);
              if (isAdded) {
                handleRemove();
              } else {
                checkPrescribed();
              }
            }}
            id={
              index || index === 0
                ? `add-cart-button-${index + 1}`
                : id || "add-cart-button-" + directId
            }
          >
            {!isAdded || type === "homecare" || type === "doctor" ? (
              <img
                src={iconImage[icon]}
                alt="cart"
                className="2xl:max-w-[80%] max-w-[70%] animate-scale-in 2xl:max-h-[80%] max-h-[70%] object-contain"
              />
            ) : deleteLoader ? (
              <Loader2 className="animate-spin" size={12} />
            ) : !deleteLoader && (
              <Trash2 size={17} className="animate-scale-in" />
            )}
            {isLoading && <Loader2 className="animate-spin" size={14} />}
          </div>
        </Tooltip>
      ) : (
        <CounterComponent
          bgColor
          productId={productId}
          type={type}
          updateCount={() => {}}
          stockQuantity={quantity}
          id={
            index || index === 0
              ? `counter-${index + 1}`
              : id || "counter-" + directId
          }
        />
      )}
    </>
  );
};

export default AddCartButton;
